'use client';

import { useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { toast } from '@kit/ui/sonner';

import {
  DEFAULT_RADIUS_KM,
  formatNear,
  roundCoord,
} from '../_lib/location-params';
import {
  readStoredLocation,
  saveLocationOff,
  saveStoredLocation,
} from '../_lib/location-preference';

/**
 * Auto-applies the buyer's location on the marketplace browse page
 * (FB-Marketplace model), CLIENT-side after the deterministic SSR render so
 * crawlers and the SSR cache never see per-visitor results.
 *
 * On mount, when the URL has no `near` param:
 *   1. localStorage preference (saved by the location filter or a previous
 *      auto-detect) → re-apply via router.replace. No toast — this is the
 *      buyer's own earlier explicit choice, not a new surprise.
 *   2. Explicit off-flag (buyer cleared the filter) → do nothing.
 *   3. No preference → IP geolocation (/api/geolocation, same endpoint the
 *      LocationAutocomplete fallback uses) → apply + persist + a toast
 *      naming the detected city with a one-click way to turn it off (BAL-500
 *      review: since the grid now actually HONOURS this filter, silently
 *      narrowing every first-time visitor's results needs to be visible, not
 *      just discoverable via the sidebar). Never prompts for
 *      browser-geolocation permission — the filter's "locate me" button is
 *      the opt-in for precise location.
 *
 * Renders nothing.
 */
export function MarketplaceLocationInit() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useTranslation();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (searchParams.get('near')) return;

    const applyLocation = (
      city: string,
      lat: number,
      lng: number,
      radius: number,
    ) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set('near', formatNear({ lat, lng }));
      params.set('radius', String(radius));
      if (city) params.set('city', city);
      params.delete('cursor');
      router.replace(`/marketplace?${params.toString()}`, { scroll: false });
    };

    const stored = readStoredLocation();
    if (stored) {
      if ('off' in stored) return;
      applyLocation(stored.city, stored.lat, stored.lng, stored.radius);
      return;
    }

    // First visit: IP-based city-level detection, fail-soft (unfiltered
    // browse is the fallback, never an error).
    const controller = new AbortController();
    void (async () => {
      try {
        const response = await fetch('/api/geolocation', {
          signal: controller.signal,
        });
        if (!response.ok) return;
        const data = (await response.json()) as {
          city?: string | null;
          latitude?: number | null;
          longitude?: number | null;
        };
        if (
          typeof data.latitude !== 'number' ||
          typeof data.longitude !== 'number'
        ) {
          return;
        }
        const lat = roundCoord(data.latitude);
        const lng = roundCoord(data.longitude);
        const city = data.city ?? '';
        saveStoredLocation({
          city,
          lat,
          lng,
          radius: DEFAULT_RADIUS_KM,
        });
        applyLocation(city, lat, lng, DEFAULT_RADIUS_KM);
        // Surface the auto-applied filter (BAL-500): it now actually narrows
        // the grid, so a silent first-visit narrowing would be a surprise.
        toast.info(
          t('explore:locationFilter.autoDetected', {
            city: city || t('explore:locationFilter.currentLocation'),
          }),
          {
            description: t('explore:locationFilter.autoDetectedDescription'),
            action: {
              label: t('explore:locationFilter.clearAriaLabel'),
              onClick: () => {
                saveLocationOff();
                const cleared = new URLSearchParams(searchParams.toString());
                cleared.delete('near');
                cleared.delete('radius');
                cleared.delete('city');
                router.replace(
                  cleared.toString()
                    ? `/marketplace?${cleared.toString()}`
                    : '/marketplace',
                  { scroll: false },
                );
              },
            },
          },
        );
      } catch {
        // Aborted or network failure — stay unfiltered.
      }
    })();

    return () => controller.abort();
    // Intentionally mount-only: this is a one-shot init, not a param watcher.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
